import React, { useState, useEffect, useRef } from 'react';
import '../styles/notifications.scss';
import CloseIcon from '@mui/icons-material/Close';
import { IconButton, Badge } from '@mui/material';
import { getNotifications, markAllNotificationsAsRead, markNotificationAsRead } from './api';
import { $pushNotifications, setPushNotification } from '../store/pushNotifications';
import { useUnit } from 'effector-react';
import { timeAgoModern } from '../shared/utils';

export const NotificationsList = ({ isOpen, onClose, anchorEl }) => {
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [position, setPosition] = useState({ bottom: 0, right: 0 });
  const notificationsRef = useRef(null);

  const pushNotifications = useUnit($pushNotifications);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (!isMobile && anchorEl && isOpen) {
      const rect = anchorEl.getBoundingClientRect();
      setPosition({
        bottom: window.innerHeight - rect.top + 8,
        right: window.innerWidth - rect.right - 4
      });
    }
  }, [isMobile, anchorEl, isOpen]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!isMobile &&
        notificationsRef.current &&
        !notificationsRef.current.contains(event.target) &&
        anchorEl &&
        !anchorEl.contains(event.target)) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isMobile, anchorEl, onClose, isOpen]);

  useEffect(() => {
    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen, onClose]);

  // Блокировка скролла body для мобильной версии
  useEffect(() => {
    if (isMobile && isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isMobile, isOpen]);

  const markAsRead = (id) => {
    markNotificationAsRead({ id: id })
      .then(_ => {
        getNotifications()
          .then(data => {
            setPushNotification(data);
          })
          .catch(error => {
            console.log(error);
          })
      })
      .catch(error => {
        console.log(error);
      });
  };

  const markAllAsRead = () => {
    const unreadNotifications = pushNotifications
      .filter(notification => !notification.read)
      .map(notification => notification.id);
    markAllNotificationsAsRead({ ids: unreadNotifications })
      .then(_ => {
        getNotifications()
          .then(data => {
            setPushNotification(data);
          })
          .catch(error => {
            console.log(error);
          })
      })
      .catch(error => {
        console.log(error);
      });
  };

  const unreadCount = pushNotifications.filter(n => !n.isRead).length;

  if (!isOpen) return null;

  return (
    <>
      {isMobile && isOpen && (
        <div className="notifications__overlay" onClick={onClose} />
      )}

      <div
        ref={notificationsRef}
        className={`notifications ${isMobile ? 'notifications--mobile' : 'notifications--desktop'}`}
        style={!isMobile ? {
          position: 'fixed',
          bottom: position.bottom,
          right: position.right,
          zIndex: 1000
        } : {}}
      >
        {!isMobile && <div className="notifications__arrow" />}

        <div className="notifications__header">
          <div className="notifications__header-left">
            <h3 className="notifications__title">Уведомления</h3>
            {unreadCount > 0 && (
              <Badge
                badgeContent={unreadCount}
                color="primary"
                className="notifications__badge"
              />
            )}
          </div>
          <div className="notifications__header-right">
            {unreadCount > 0 && (
              <button
                className="notifications__mark-read"
                onClick={markAllAsRead}
              >
                Прочитать все
              </button>
            )}
            {isMobile && (
              <IconButton onClick={onClose} className="notifications__close">
                <CloseIcon />
              </IconButton>
            )}
          </div>
        </div>

        <div className="notifications__list">
          {pushNotifications.length === 0 ? (
            <div className="notifications__empty">
              <p>Нет новых уведомлений</p>
            </div>
          ) : (
            pushNotifications.map(notification => (
              <div
                key={notification.id}
                className={`notifications__item ${!notification.isRead ? 'notifications__item--unread' : ''}`}
                onClick={() => markAsRead(notification.id)}
              >
                <div className="notifications__item-content">
                  <div className="notifications__item-info">
                    <p className="notifications__item-message">
                      <b>{notification.title}</b>
                    </p>
                    <p className="notifications__item-message">
                      {notification.message}
                    </p>
                    <span className="notifications__item-time">
                      {timeAgoModern(notification.createdAt)}
                    </span>
                  </div>
                  {!notification.isRead && (
                    <span className="notifications__item-unread-dot" />
                  )}
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </>
  );
};